/**
 * media-gallery-thumbnail-nav.js
 *
 * Makes the prev/next arrows on the thumbnail slider (GalleryThumbnails-*)
 * also change the main product image, not just scroll the thumbnail strip.
 * Arrows are disabled (and dimmed to 50% opacity) exactly at the first and
 * last thumbnail, based on which thumbnail is actually active — not the
 * theme's own scroll-position-based disabling, which can trigger a few
 * items early near the edges of the list.
 *
 * It works by finding the currently active thumbnail (aria-current="true")
 * and simulating a click on the next/previous thumbnail <button>. Your
 * existing media-gallery.js already listens for clicks on .thumbnail
 * buttons to swap the main image, so this re-uses that logic instead of
 * duplicating it.
 *
 * Load this AFTER media-gallery.js, e.g. in theme.liquid:
 *   {{ 'media-gallery.js' | asset_url | script_tag }}
 *   {{ 'media-gallery-thumbnail-nav.js' | asset_url | script_tag }}
 */
(function () {
  function initThumbnailNav(thumbSlider) {
    // Avoid double-binding if this element gets initialized more than once
    // (e.g. both shopify:section:load and variation:swapped fire for it).
    if (thumbSlider.dataset.thumbNavInit === 'true') return;
    thumbSlider.dataset.thumbNavInit = 'true';

    const prevBtn = thumbSlider.querySelector('.slider-button--prev');
    const nextBtn = thumbSlider.querySelector('.slider-button--next');
    if (!prevBtn && !nextBtn) return;

    let desiredPrevDisabled = false;
    let desiredNextDisabled = false;

    function getThumbButtons() {
      return Array.from(thumbSlider.querySelectorAll('.thumbnail-list__item .thumbnail'));
    }

    function getActiveIndex(buttons) {
      const idx = buttons.findIndex((btn) => btn.getAttribute('aria-current') === 'true');
      return idx === -1 ? 0 : idx;
    }

    function applyDisabledState(btn, disabled) {
      if (!btn) return;
      btn.disabled = disabled;
      btn.style.opacity = disabled ? '0.5' : '';
      btn.style.pointerEvents = disabled ? 'none' : '';
      btn.setAttribute('aria-disabled', String(disabled));
    }

    function updateButtonStates() {
      const buttons = getThumbButtons();
      const idx = getActiveIndex(buttons);
      desiredPrevDisabled = idx <= 0;
      desiredNextDisabled = idx >= buttons.length - 1;
      applyDisabledState(prevBtn, desiredPrevDisabled);
      applyDisabledState(nextBtn, desiredNextDisabled);
    }

    // The theme's own slider-component script also toggles the disabled
    // state on these buttons based on scroll position, which can fight
    // with our own logic. Whenever it changes something, snap the button
    // back to whatever OUR active-thumbnail logic says it should be.
    function guard(btn, getDesired) {
      if (!btn) return;
      const observer = new MutationObserver(() => {
        const shouldBeDisabled = getDesired();
        if (btn.disabled !== shouldBeDisabled) {
          applyDisabledState(btn, shouldBeDisabled);
        }
      });
      observer.observe(btn, { attributes: true, attributeFilter: ['disabled', 'class', 'style'] });
    }
    guard(prevBtn, () => desiredPrevDisabled);
    guard(nextBtn, () => desiredNextDisabled);

    function activateThumbnail(index) {
      const buttons = getThumbButtons();
      if (!buttons.length || index < 0 || index >= buttons.length) return;
      const target = buttons[index];
      // Reuse the theme's existing click handling to swap the main image
      target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      updateButtonStates();
    }

    prevBtn?.addEventListener('click', () => {
      const buttons = getThumbButtons();
      activateThumbnail(getActiveIndex(buttons) - 1);
    });

    nextBtn?.addEventListener('click', () => {
      const buttons = getThumbButtons();
      activateThumbnail(getActiveIndex(buttons) + 1);
    });

    // Keep state correct if a thumbnail is clicked directly (not via arrows)
    thumbSlider.addEventListener('click', (event) => {
      if (event.target.closest('.thumbnail')) {
        window.setTimeout(updateButtonStates, 50);
      }
    });

    updateButtonStates();
  }

  function initAll(root) {
    root.querySelectorAll('slider-component[id^="GalleryThumbnails-"]').forEach(initThumbnailNav);
  }

  initAll(document);

  // Handle sections added later (theme editor, dynamic sections, quick-add modals)
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  // Handle the custom AJAX variation swap (VariationSwatches), which replaces
  // MainProduct-{sectionId}'s innerHTML and dispatches this event instead of
  // Shopify's own shopify:section:load.
  document.addEventListener('variation:swapped', () => {
    initAll(document);
  });
})();